import { addLineItemsToCurrentCart } from '@wix/ambassador-ecom-v2-cart/http';
import { searchOrders } from '@wix/ambassador-ecom-v1-order/http';
import type { Image } from '@wix/ambassador-ecom-v1-order/types';
import { queryProducts } from '@wix/ambassador-stores-catalog-v1-product/http';
import { WIX_NEW_STORES, WIX_STORES } from '@wix/app-definition-ids';
import type { IHttpClient } from '@wix/yoshi-flow-editor';

const WIX_IMAGE_PREFIX = 'wix:image://v1/';

export interface OrderItem {
  catalogItemId: string;
  productName: string;
  formattedPrice: string;
  imageUrl?: string;
  catalogOptions?: Record<string, any>;
  inStock?: boolean;
}

const buildStaticMediaUrl = (mediaId: string): string =>
  `https://static.wixstatic.com/media/${mediaId}`;

const toImageUrl = (image: Image | undefined): string | undefined => {
  if (!image) {
    return undefined;
  }
  if (image.url) {
    return image.url;
  }
  if (image.id) {
    if (image.id.startsWith(WIX_IMAGE_PREFIX)) {
      const mediaId = image.id.slice(WIX_IMAGE_PREFIX.length).split('/')[0];
      return buildStaticMediaUrl(mediaId);
    }
    return buildStaticMediaUrl(image.id);
  }
  return undefined;
};

export async function fetchOrderItems(
  httpClient: IHttpClient,
): Promise<OrderItem[]> {
  const { data } = await httpClient.request(
    searchOrders({
      search: {
        filter: { paymentStatus: 'PAID' },
        cursorPaging: { limit: 1 },
      },
    }),
  );

  const items: OrderItem[] = [];
  const seen = new Set<string>();

  for (const lineItem of data.orders?.[0]?.lineItems ?? []) {
    const catalogItemId = lineItem.catalogReference?.catalogItemId;
    const appId = lineItem.catalogReference?.appId;
    if (!catalogItemId || (appId !== WIX_STORES && appId !== WIX_NEW_STORES) || seen.has(catalogItemId)) {
      continue;
    }
    seen.add(catalogItemId);
    const options = lineItem.catalogReference?.options as
      | Record<string, any>
      | undefined;
    items.push({
      catalogItemId,
      productName:
        lineItem.productName?.translated ??
        lineItem.productName?.original ??
        '',
      formattedPrice: lineItem.price?.formattedAmount ?? '',
      imageUrl: toImageUrl(lineItem.image),
      ...(options && Object.keys(options).length > 0 && { catalogOptions: options }),
    });
  }

  return items;
}

export async function enrichWithStockStatus(
  httpClient: IHttpClient,
  items: OrderItem[],
): Promise<OrderItem[]> {
  if (items.length === 0) {
    return items;
  }
  try {
    const ids = items.map((item) => item.catalogItemId);
    const { data } = await httpClient.request(
      queryProducts({
        query: {
          filter: JSON.stringify({ id: { $hasSome: ids } }),
          paging: { limit: ids.length },
        },
      }),
    );
    const stockMap = new Map<string, boolean>();
    for (const product of data.products ?? []) {
      if (product.id) {
        stockMap.set(product.id, product.stock?.inventoryStatus !== 'OUT_OF_STOCK');
      }
    }
    return items.map((item) => ({
      ...item,
      inStock: stockMap.get(item.catalogItemId) ?? true,
    }));
  } catch {
    return items.map((item) => ({ ...item, inStock: true }));
  }
}

export async function addToCart(
  httpClient: IHttpClient,
  catalogItemId: string,
  catalogOptions?: Record<string, any>,
): Promise<void> {
  await httpClient.request(
    addLineItemsToCurrentCart({
      catalogItems: [
        {
          catalogReference: {
            catalogItemId,
            appId: WIX_NEW_STORES,
            ...(catalogOptions && { options: catalogOptions }),
          },
          quantity: 1,
        },
      ],
    }),
  );
}
